/**
 * A company's logo: fetching it, showing it, replacing it.
 *
 * Stored as a Document with doc_type company_logo, which is the third
 * ownership shape those routes express — company_id is the owner, and neither
 * candidate_id nor employee_id is set. No second upload path and no new table;
 * the profile photo already established this and the logo follows it.
 *
 * Two exports, because two callers want different halves. `CompanyLogo`
 * displays one and is used on the card. `CompanyLogoField` displays and
 * replaces one and is used on the edit form. The add form uses neither: a
 * logo needs a company to belong to, so it is uploaded after creation rather
 * than staged through it.
 *
 * The blob has to be fetched with the Authorization header, so it cannot be a
 * plain <img src>. Fetched once per document id and revoked on unmount —
 * an object URL that is never revoked is a leak that only shows up on a
 * screen somebody leaves open.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import type { Page, StoredDocument } from '../types';

/** Images only, matching what the storage layer already accepts for photos. */
const LOGO_ACCEPT = '.png,.jpg,.jpeg,.webp';
/** The server's own cap; checked here to save a refused round trip. */
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * The company's logo document, or null.
 *
 * Newest first and take the first: replacing a logo uploads a new document
 * rather than overwriting the old file, so the latest upload is the current
 * logo and the earlier ones are history.
 */
function useLogoDocument(companyId: string) {
  return useQuery<Page<StoredDocument>>({
    queryKey: ['company-logo', companyId],
    queryFn: async () =>
      (
        await client.get('/api/v1/documents', {
          params: { docType: 'company_logo', companyId, limit: 5 },
          // The document is the named company's, not the acting one's — an
          // owner editing a company they are not currently acting as must
          // still see its logo.
          skipActingCompany: true,
        })
      ).data,
  });
}

/** An object URL for a document, revoked when it changes or unmounts. */
function useDocumentImage(documentId: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      setUrl(null);
      return;
    }
    let stale = false;
    let created: string | null = null;
    void client
      .get(`/api/v1/documents/${documentId}/download`, { responseType: 'blob' })
      .then((response) => {
        if (stale) return;
        created = URL.createObjectURL(response.data as Blob);
        setUrl(created);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [documentId]);

  return url;
}

/** Just the image, for a card. */
export function CompanyLogo({
  companyId,
  className = 'h-8 w-8',
}: {
  companyId: string;
  className?: string;
}) {
  const { data } = useLogoDocument(companyId);
  const logo = data?.items[0];
  const url = useDocumentImage(logo?.id);

  // The building icon rather than a broken frame or a gap: most companies
  // will have no logo for a while, and an empty box reads as a failure.
  if (!url) {
    return <Building2 className={`${className} text-indigo-600`} />;
  }
  return (
    <img
      src={url}
      alt=""
      className={`${className} rounded object-contain`}
    />
  );
}

/** The image plus replace and remove, for the edit form. */
export function CompanyLogoField({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useLogoDocument(companyId);
  const logo = data?.items[0];
  const url = useDocumentImage(logo?.id);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['company-logo', companyId] });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      // snake_case by hand: the request interceptor leaves FormData keys
      // alone, because converting one means rebuilding it and File entries do
      // not survive that.
      body.append('file', file);
      body.append('doc_type', 'company_logo');
      return (
        await client.post('/api/v1/documents', body, {
          params: { company_id: companyId },
          skipActingCompany: true,
        })
      ).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Logo updated');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not upload the logo')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) =>
      (await client.delete(`/api/v1/documents/${id}`)).data,
    onSuccess: () => {
      invalidate();
      toast.success('Logo removed');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not remove the logo')),
  });

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-gray-200">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        ) : url ? (
          <img src={url} alt="" className="max-h-14 max-w-14 object-contain" />
        ) : (
          <Building2 className="h-6 w-6 text-gray-300" />
        )}
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <label className="btn-secondary cursor-pointer text-xs">
            {upload.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {logo ? 'Replace' : 'Upload'}
            <input
              type="file"
              className="hidden"
              accept={LOGO_ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first, so choosing the same file twice fires again.
                e.target.value = '';
                if (!file) return;
                if (file.size > MAX_BYTES) {
                  toast.error('That file is over 10MB.');
                  return;
                }
                upload.mutate(file);
              }}
            />
          </label>
          {logo && (
            <button
              type="button"
              className="btn-ghost text-xs text-red-600"
              disabled={remove.isPending}
              onClick={() => remove.mutate(logo.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
        </div>
        <p className="mt-1 text-[11px] text-gray-400">
          PNG or JPEG, up to 10MB. Shown on this company&rsquo;s card and at the
          head of invoices raised from now on.
        </p>
      </div>
    </div>
  );
}
